import {ArrowDownIcon, ArrowUpIcon} from 'icons';
import {useEffect, useState} from 'react';
import {VoteType} from 'types/vote';
import {useUpsell} from 'users';
import {classNames} from 'utils';
import {AsyncButton} from './async_button';
import ToolTip from './tooltip';
import styles from './vote.module.css';

export const Vote = ({
  upvotes: initialUpvotes,
  userVote: initialUserVote = VoteType.Novote,
  minUpvotes,
  onVote,
  onVoteSuccess,
  layout = 'horizontal',
  hideVote = false,
  disabled = false,
}: Props) => {
  const {loginRequired} = useUpsell();
  const [upvotes, setUpvotes] = useState(initialUpvotes);
  const [userVote, setUserVote] = useState(initialUserVote);

  // Update internal state if props change.
  useEffect(() => setUserVote(initialUserVote), [initialUserVote]);
  useEffect(() => setUpvotes(initialUpvotes), [initialUpvotes]);

  const upvoted = userVote === VoteType.Upvote;
  const downvoted = userVote === VoteType.Downvote;

  const handleVoteSuccess = (newUserVote: VoteType) => {
    // Update internal state after voting.
    const newUpvotes = upvotes - userVote + newUserVote;
    setUpvotes(newUpvotes);
    setUserVote(newUserVote);
    if (onVoteSuccess) onVoteSuccess(newUserVote, newUpvotes);
  };

  return (
    <div className={classNames([styles.vote, styles[layout]])}>
      <ToolTip tipText={upvoted ? 'Remove Upvote' : 'Upvote'} disabled={disabled}>
        <AsyncButton
          title={upvoted ? 'Remove Upvote' : 'Upvote'}
          name='upvote'
          type='icon'
          allowClick={loginRequired({cta: 'vote'})}
          onRequest={() => onVote(upvoted ? VoteType.Novote : VoteType.Upvote)}
          onSuccess={handleVoteSuccess}
          disabled={disabled}>
          <ArrowUpIcon color={upvoted ? 'green' : 'greyLight'} />
        </AsyncButton>
      </ToolTip>
      {!hideVote && (
        <div className={styles.voteCount}>
          {minUpvotes === undefined ? upvotes : Math.max(minUpvotes, upvotes)}
        </div>
      )}
      <ToolTip tipText={downvoted ? 'Remove Downvote' : 'Downvote'} disabled={disabled}>
        <AsyncButton
          title={downvoted ? 'Remove Downvote' : 'Downvote'}
          name='downvote'
          type='icon'
          allowClick={loginRequired({cta: 'vote'})}
          onRequest={() => onVote(downvoted ? VoteType.Novote : VoteType.Downvote)}
          onSuccess={handleVoteSuccess}
          disabled={disabled}>
          <ArrowDownIcon color={downvoted ? 'red' : 'greyLight'} />
        </AsyncButton>
      </ToolTip>
    </div>
  );
};

type Props = {
  upvotes: number;
  userVote: VoteType;
  minUpvotes?: number;
  onVote: (vote: VoteType) => Promise<VoteType>;
  onVoteSuccess?: (userVote: VoteType, upvotes: number) => void;
  layout?: 'horizontal' | 'vertical';
  hideVote?: boolean;
  disabled?: boolean;
};
